import { NO_ICON_MATCH } from "@shared/icon-catalog";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { useLiveQuery } from "dexie-react-hooks";
import { ShoppingBasket } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Spinner } from "@/components/Spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Toggle } from "@/components/ui/toggle";
import { ToggleGroup } from "@/components/ui/toggle-group";
import { type AccountInfo, getAccount } from "@/lib/account";
import { authClient } from "@/lib/auth-client";
import { db } from "@/lib/db";
import * as debug from "@/lib/debug";
import { invalidateIconPull, pullIconMapUpdates } from "@/lib/icon-map";
import type { SyncStatus } from "@/lib/sync/sync-service";
import { syncService } from "@/lib/sync/sync-service";
import { useSyncStatus } from "@/lib/sync/use-sync";

export const Route = createFileRoute("/_settings/debug")({
  // Role-gated (#201): a deep link from a user without the `debug` permission
  // goes home rather than rendering a page whose API calls would all 404.
  beforeLoad: async () => {
    const { data } = await authClient.getSession();
    if (!debug.canAccessDebug(data?.user.role)) throw redirect({ to: "/" });
  },
  component: DebugPage,
  staticData: { title: "Debug" },
});

type Section = "account" | "global";

function Panel({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
      <span className="min-w-0 truncate text-muted-foreground">{label}</span>
      <span className="shrink-0 font-medium tabular-nums">{children}</span>
    </li>
  );
}

function RowList({ children }: { children: React.ReactNode }) {
  return (
    <ul className="flex flex-col divide-y rounded-md border">{children}</ul>
  );
}

const STATUS_LABEL: Record<SyncStatus, string> = {
  connected: "Connected",
  syncing: "Syncing",
  connecting: "Connecting",
  disconnected: "Disconnected",
};

function StatusDot({ status }: { status: SyncStatus }) {
  const color =
    status === "connected"
      ? "bg-green-500"
      : status === "disconnected"
        ? "bg-red-500"
        : "bg-amber-500";
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`size-2 rounded-full ${color}`} />
      {STATUS_LABEL[status]}
    </span>
  );
}

function DeleteEntryButton({
  onConfirm,
}: {
  onConfirm: () => void | Promise<void>;
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger
        render={
          <button
            type="button"
            className="text-xs text-destructive underline underline-offset-2"
          />
        }
      >
        Delete
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this cache entry?</AlertDialogTitle>
          <AlertDialogDescription>
            This is <strong>global</strong> reference data — deleting it affects
            every account. It will be re-resolved the next time it's needed.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => void onConfirm()}>
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Button + "Are you sure?" confirmation for a one-off (bulk) action. */
function ConfirmAction({
  render,
  children,
  title,
  description,
  confirmLabel,
  destructive,
  onConfirm,
}: {
  render: React.ReactElement;
  children: React.ReactNode;
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: () => void | Promise<void>;
}) {
  // Controlled: unlike DeleteEntryButton (whose row unmounts on delete), these
  // triggers persist, so AlertDialogAction — a plain Button, not a Close — won't
  // dismiss the dialog on its own. Close it explicitly on confirm.
  const [open, setOpen] = useState(false);
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger render={render}>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant={destructive ? "destructive" : undefined}
            onClick={() => {
              setOpen(false);
              void onConfirm();
            }}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DebugPage() {
  const [section, setSection] = useState<Section>("account");
  const { data: session } = authClient.useSession();
  const status = useSyncStatus();

  return (
    <div className="flex flex-col gap-6">
      <ToggleGroup
        aria-label="Debug section"
        value={[section]}
        onValueChange={(values) => {
          const next = values[0];
          if (next) setSection(next as Section);
        }}
      >
        <Toggle value="account">Account state</Toggle>
        <Toggle value="global">Global state</Toggle>
      </ToggleGroup>

      {section === "account" ? (
        <AccountState status={status} accountId={session?.user.accountId} />
      ) : (
        <GlobalState
          canManage={debug.canManageGlobalDebug(session?.user.role)}
        />
      )}
    </div>
  );
}

function AccountState({
  status,
  accountId,
}: {
  status: SyncStatus;
  accountId: string | undefined;
}) {
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [server, setServer] = useState<debug.DebugState | null>(null);
  const [serverError, setServerError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Guards against overlapping fetches (manual refresh vs. the poll below).
  const inFlight = useRef(false);

  const refreshServer = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setRefreshing(true);
    try {
      setServer(await debug.getDebugState());
      setServerError(false);
    } catch {
      setServerError(true);
    } finally {
      setRefreshing(false);
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    void getAccount()
      .then(setAccount)
      .catch(() => {});
    void refreshServer();
  }, [refreshServer]);

  // Poll the server backlog so progress (e.g. after "Re-analyse all recipes")
  // is visible without manually refreshing.
  useEffect(() => {
    const id = setInterval(() => void refreshServer(), 2500);
    return () => clearInterval(id);
  }, [refreshServer]);

  // Live local (Dexie) counts.
  const counts = useLiveQuery(
    async () => ({
      shoppingItems: await db.shoppingItems.count(),
      removedItems: await db.removedItems.count(),
      recipes: await db.recipes.count(),
      mealPlanEntries: await db.mealPlanEntries.count(),
      userSettings: await db.userSettings.count(),
      iconMap: await db.iconMap.count(),
      unsent: await db.changeLog.where("sent").equals(0).count(),
      inFlight: await db.changeLog.where("sent").equals(1).count(),
    }),
    [],
  );
  const syncStateRow = useLiveQuery(() => db.syncState.get("current"), []);

  async function handleDrain() {
    try {
      await debug.triggerDrain();
      toast.success("Server drain triggered.");
      setTimeout(() => void refreshServer(), 1500);
    } catch {
      toast.error("Failed to trigger drain.");
    }
  }

  function handleReconnect() {
    syncService.forceReconnect();
    toast.success("Reconnecting sync…");
  }

  async function handleReanalyseAll() {
    try {
      const { recipes } = await debug.reanalyseAllRecipes();
      toast.success(
        `Re-queued ${recipes} recipe${recipes === 1 ? "" : "s"} for analysis.`,
      );
      setTimeout(() => void refreshServer(), 500);
    } catch {
      toast.error("Failed to re-analyse recipes.");
    }
  }

  async function handleRepullIcons() {
    try {
      invalidateIconPull();
      // Clear the local cache first so a re-pull rebuilds from server truth and
      // drops entries no longer present (a plain re-pull only upserts, so stale
      // rows would otherwise linger).
      await db.iconMap.clear();
      await db.iconMapState.delete("current");
      await pullIconMapUpdates();
      toast.success("Icon map re-pulled.");
    } catch {
      toast.error("Icon map re-pull failed.");
    }
  }

  const doState = server?.sessionAccountDO;

  return (
    <div className="flex flex-col gap-6">
      <Panel title="Account">
        <RowList>
          <Row label="Account ID">
            <span className="font-mono text-xs">{accountId ?? "—"}</span>
          </Row>
          {server && !server.identity.match && (
            <Row label="⚠ Session vs DB account">mismatch</Row>
          )}
        </RowList>
        <p className="text-sm text-muted-foreground">Members with access</p>
        <RowList>
          {account?.members.map((m) => (
            <Row key={m.userId} label={m.name ?? m.email}>
              {m.role === "owner" ? "owner" : "member"}
              {m.isSelf ? " · you" : ""}
            </Row>
          ))}
          {!account && (
            <li className="px-3 py-2 text-sm text-muted-foreground">
              Loading…
            </li>
          )}
        </RowList>
      </Panel>

      <Panel title="Sync">
        <RowList>
          <Row label="WebSocket">
            <StatusDot status={status} />
          </Row>
          <Row label="Last server version">
            {syncStateRow?.lastServerVersion ?? 0}
          </Row>
          <Row label="Unsent local changes">{counts?.unsent ?? "…"}</Row>
          <Row label="In-flight (awaiting ack)">{counts?.inFlight ?? "…"}</Row>
        </RowList>
      </Panel>

      <Panel title="Local store (this device)">
        <RowList>
          <Row label="Shopping items">{counts?.shoppingItems ?? "…"}</Row>
          <Row label="Removed items">{counts?.removedItems ?? "…"}</Row>
          <Row label="Recipes">{counts?.recipes ?? "…"}</Row>
          <Row label="Meal plan entries">{counts?.mealPlanEntries ?? "…"}</Row>
          <Row label="User settings">{counts?.userSettings ?? "…"}</Row>
          <Row label="Icon map entries">{counts?.iconMap ?? "…"}</Row>
        </RowList>
      </Panel>

      <Panel
        title="Server backlog"
        action={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void refreshServer()}
            disabled={refreshing}
          >
            Refresh
          </Button>
        }
      >
        {serverError ? (
          <p className="text-sm text-muted-foreground">
            Couldn't load server state.
          </p>
        ) : !doState ? (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        ) : (
          <RowList>
            <Row label="Shopping items awaiting analysis">
              {doState.pendingCategorization.count}
            </Row>
            <Row label="Ingredient lines awaiting analysis">
              {doState.pendingIngredientAnalysis.lineCount}
            </Row>
            <Row label="Recipes queued for analysis">
              {doState.pendingIngredientAnalysis.count}
            </Row>
            <Row label="Recipes (live / partly / unanalysed)">
              {`${doState.recipes.live} / ${doState.recipes.partlyAnalysed} / ${doState.recipes.unanalysed}`}
            </Row>
            <Row label="Categorization retries (max / deferred)">
              {`${doState.pendingCategorization.retries} / ${doState.pendingCategorization.deferred}`}
            </Row>
            <Row label="Ingredient analysis retries (max / deferred)">
              {`${doState.pendingIngredientAnalysis.retries} / ${doState.pendingIngredientAnalysis.deferred}`}
            </Row>
            <Row label="Live server sockets">{doState.liveSockets}</Row>
            <Row label="Server version">{doState.version}</Row>
            <Row label="Next alarm">
              {/* getAlarm() is null while the alarm handler is mid-run (the
                  runtime clears it before invoking and it's only re-set at the
                  end), so a null here means the alarm is executing right now, not
                  that none is scheduled — alarm() always re-arms. alarmInMs is a
                  server-computed delta, so deriving the target off the client's
                  clock keeps the relative label skew-free. */}
              {doState.alarmInMs == null
                ? "Running now"
                : formatDistanceToNow(
                    new Date(Date.now() + doState.alarmInMs),
                    {
                      addSuffix: true,
                    },
                  )}
            </Row>
          </RowList>
        )}
      </Panel>

      <Panel title="Actions">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={handleReconnect}>
            Force reconnect
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleDrain()}
          >
            Trigger server drain
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleRepullIcons()}
          >
            Re-pull icon map
          </Button>
          <ConfirmAction
            render={<Button variant="outline" size="sm" />}
            title="Re-analyse all recipes?"
            description="Clears the AI-derived fields on every recipe's ingredients and re-queues them all for analysis. This costs Gemini calls and discards any manual edits to those fields."
            confirmLabel="Re-analyse"
            onConfirm={handleReanalyseAll}
          >
            Re-analyse all recipes
          </ConfirmAction>
        </div>
      </Panel>
    </div>
  );
}

const AI_PREFIXES: { value: debug.AiCachePrefix; label: string }[] = [
  { value: "cat:v2:", label: "Categorization" },
  { value: "split:", label: "Ingredient split" },
  { value: "ingr:", label: "Ingredient parse" },
];

/**
 * The global (cross-account) caches. `canManage` (admin only, #212) enables the
 * delete actions; without it the caches are read-only, and the server refuses
 * the deletes anyway.
 */
function GlobalState({ canManage }: { canManage: boolean }) {
  const [iconEntries, setIconEntries] = useState<debug.IconCacheEntry[]>([]);
  const [iconOffset, setIconOffset] = useState(0);
  const [iconHasMore, setIconHasMore] = useState(false);
  const [iconSearch, setIconSearch] = useState("");

  const [aiPrefix, setAiPrefix] = useState<debug.AiCachePrefix>("cat:v2:");
  const [aiKeys, setAiKeys] = useState<string[]>([]);
  const [aiCursor, setAiCursor] = useState<string | null>(null);
  const [aiHasMore, setAiHasMore] = useState(false);
  const [aiSearch, setAiSearch] = useState("");

  // Icon-cache load, debounced, re-run when the search term changes.
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      void debug
        .listIconCache(0, iconSearch || undefined)
        .then((page) => {
          if (cancelled) return;
          setIconEntries(page.mappings);
          setIconOffset(page.nextOffset);
          setIconHasMore(page.hasMore);
        })
        .catch(() => {
          if (!cancelled) toast.error("Failed to load icon cache.");
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [iconSearch]);

  // AI-cache load, debounced, re-run on prefix or search change.
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      void debug
        .listAiCache(aiPrefix, undefined, aiSearch || undefined)
        .then((page) => {
          if (cancelled) return;
          setAiKeys(page.keys.map((k) => k.name));
          setAiCursor(page.cursor);
          setAiHasMore(!page.listComplete);
        })
        .catch(() => {
          if (!cancelled) toast.error("Failed to load AI cache.");
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [aiPrefix, aiSearch]);

  async function loadMoreIcons() {
    try {
      const page = await debug.listIconCache(
        iconOffset,
        iconSearch || undefined,
      );
      setIconEntries((prev) => [...prev, ...page.mappings]);
      setIconOffset(page.nextOffset);
      setIconHasMore(page.hasMore);
    } catch {
      toast.error("Failed to load icon cache.");
    }
  }

  async function loadMoreAi() {
    if (!aiCursor) return;
    try {
      const page = await debug.listAiCache(
        aiPrefix,
        aiCursor,
        aiSearch || undefined,
      );
      setAiKeys((prev) => [...prev, ...page.keys.map((k) => k.name)]);
      setAiCursor(page.cursor);
      setAiHasMore(!page.listComplete);
    } catch {
      toast.error("Failed to load AI cache.");
    }
  }

  async function handleDeleteIcon(groupAs: string) {
    try {
      await debug.deleteIconCacheEntry(groupAs);
      setIconEntries((prev) => prev.filter((e) => e.groupAs !== groupAs));
      toast.success("Icon mapping deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  }

  async function handleDeleteAi(key: string) {
    try {
      await debug.deleteAiCacheEntry(key);
      setAiKeys((prev) => prev.filter((k) => k !== key));
      toast.success("Cache entry deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  }

  async function handleDeleteAllIcons() {
    try {
      const n = await debug.deleteAllIconCache(iconSearch || undefined);
      setIconEntries([]);
      setIconOffset(0);
      setIconHasMore(false);
      toast.success(`Deleted ${n} icon mapping${n === 1 ? "" : "s"}.`);
    } catch {
      toast.error("Delete failed.");
    }
  }

  async function handleDeleteAllAi() {
    try {
      const n = await debug.deleteAllAiCache(aiPrefix, aiSearch || undefined);
      setAiKeys([]);
      setAiCursor(null);
      setAiHasMore(false);
      toast.success(`Deleted ${n} cache entr${n === 1 ? "y" : "ies"}.`);
    } catch {
      toast.error("Delete failed.");
    }
  }

  const currentAiLabel =
    AI_PREFIXES.find((p) => p.value === aiPrefix)?.label ?? "AI";

  return (
    <div className="flex flex-col gap-6">
      <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-muted-foreground">
        These caches are <strong>global</strong> — shared across every account.
        {canManage
          ? " Deleting an entry affects all households and forces a re-resolve."
          : " They're read-only here: deleting entries needs the Admin role."}
      </p>

      <Panel
        title="Icon cache (groupAs → icon)"
        action={
          canManage && (
            <ConfirmAction
              render={
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  disabled={iconEntries.length === 0}
                />
              }
              title={
                iconSearch
                  ? "Delete all matching icon mappings?"
                  : "Delete all icon mappings?"
              }
              description={
                <>
                  This is <strong>global</strong> reference data — deleting
                  affects every account.{" "}
                  {iconSearch
                    ? `Every mapping matching “${iconSearch}” (including any not loaded here) will be deleted`
                    : "Every icon mapping will be deleted"}{" "}
                  and re-resolved on the next categorisation.
                </>
              }
              confirmLabel="Delete all"
              destructive
              onConfirm={handleDeleteAllIcons}
            >
              Delete all
            </ConfirmAction>
          )
        }
      >
        <Input
          type="search"
          placeholder="Filter by groupAs or icon…"
          value={iconSearch}
          onChange={(e) => setIconSearch(e.target.value)}
        />
        <RowList>
          {iconEntries.map((e) => (
            <li
              key={e.groupAs}
              className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
            >
              <span className="flex min-w-0 items-center gap-2">
                {e.icon === NO_ICON_MATCH ? (
                  <ShoppingBasket className="size-6 shrink-0 text-muted-foreground/40" />
                ) : (
                  <img
                    src={`/food/${e.icon}.svg`}
                    alt=""
                    className="size-6 shrink-0 drop-shadow-sm dark:drop-shadow-none"
                  />
                )}
                <span className="min-w-0 truncate">
                  <span className="font-mono text-xs">{e.groupAs}</span>
                  <span className="text-muted-foreground"> → {e.icon}</span>
                </span>
              </span>
              {canManage && (
                <DeleteEntryButton
                  onConfirm={() => handleDeleteIcon(e.groupAs)}
                />
              )}
            </li>
          ))}
          {iconEntries.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted-foreground">
              {iconSearch ? "No matches." : "No entries."}
            </li>
          )}
        </RowList>
        {iconHasMore && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void loadMoreIcons()}
          >
            Load more
          </Button>
        )}
      </Panel>

      <Panel
        title="AI result cache"
        action={
          canManage && (
            <ConfirmAction
              render={
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  disabled={aiKeys.length === 0}
                />
              }
              title={
                aiSearch
                  ? `Delete all matching ${currentAiLabel} entries?`
                  : `Delete all ${currentAiLabel} entries?`
              }
              description={
                <>
                  This is a <strong>global</strong> cache — deleting affects
                  every account.{" "}
                  {aiSearch
                    ? `Every ${currentAiLabel} entry matching “${aiSearch}” (including any not loaded here) will be deleted`
                    : `Every ${currentAiLabel} entry will be deleted`}{" "}
                  and re-computed on next use. Other cache types are unaffected.
                </>
              }
              confirmLabel="Delete all"
              destructive
              onConfirm={handleDeleteAllAi}
            >
              Delete all
            </ConfirmAction>
          )
        }
      >
        <ToggleGroup
          aria-label="AI cache type"
          value={[aiPrefix]}
          onValueChange={(values) => {
            const next = values[0];
            if (next) setAiPrefix(next as debug.AiCachePrefix);
          }}
        >
          {AI_PREFIXES.map((p) => (
            <Toggle key={p.value} value={p.value} size="sm">
              {p.label}
            </Toggle>
          ))}
        </ToggleGroup>
        <Input
          type="search"
          placeholder="Filter by description…"
          value={aiSearch}
          onChange={(e) => setAiSearch(e.target.value)}
        />
        <RowList>
          {aiKeys.map((key) => (
            <li
              key={key}
              className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
            >
              <span className="min-w-0 truncate font-mono text-xs">
                {key.slice(aiPrefix.length)}
              </span>
              {canManage && (
                <DeleteEntryButton onConfirm={() => handleDeleteAi(key)} />
              )}
            </li>
          ))}
          {aiKeys.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted-foreground">
              {aiSearch ? "No matches." : "No entries."}
            </li>
          )}
        </RowList>
        {aiHasMore && (
          <Button variant="ghost" size="sm" onClick={() => void loadMoreAi()}>
            Load more
          </Button>
        )}
      </Panel>
    </div>
  );
}
